import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { type SpendSeries } from '@apps/web/components/admin/llm_spend/constants';
import { formatUsd } from '@apps/web/components/admin/llm_spend/spend_math';
import { type StackedSpendPoint } from '@apps/web/components/admin/llm_spend/stacked_spend_chart';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { cn } from '@apps/web/lib/utils';

const PERCENT = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
});

type SpendTotalRow = SpendSeries & {
    readonly total: number;
    readonly share: number;
};

function summarizeTotals(
    points: ReadonlyArray<StackedSpendPoint>,
    series: ReadonlyArray<SpendSeries>,
): Array<SpendTotalRow> {
    const rows = series.map((entry) => ({
        ...entry,
        total: points.reduce(
            (sum, point) => sum + (point.values[entry.key] ?? 0),
            0,
        ),
    }));
    const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);
    return rows
        .filter((row) => row.total > 0)
        .map((row) => ({
            ...row,
            share: grandTotal === 0 ? 0 : row.total / grandTotal,
        }))
        .sort(
            (left, right) =>
                right.total - left.total ||
                left.label.localeCompare(right.label),
        );
}

export function SpendTotalsTable({
    dimensionLabel,
    points,
    series,
}: {
    readonly dimensionLabel: string;
    readonly points: ReadonlyArray<StackedSpendPoint>;
    readonly series: ReadonlyArray<SpendSeries>;
}) {
    const [open, setOpen] = useState(false);
    const rows = summarizeTotals(points, series);
    if (rows.length === 0) {
        return null;
    }
    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className="border-t pt-1"
        >
            <CollapsibleTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground -ml-2 gap-1"
                >
                    <ChevronDown
                        aria-hidden="true"
                        className={cn(
                            'size-4 transition-transform',
                            open ? 'rotate-180' : '',
                        )}
                    />
                    {open ? 'Hide totals' : 'View totals'}
                </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-1">
                <Table aria-label={`Spend by ${dimensionLabel.toLowerCase()}`}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{dimensionLabel}</TableHead>
                            <TableHead className="text-right">Spend</TableHead>
                            <TableHead className="text-right">Share</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.key}>
                                <TableCell>
                                    <span className="flex items-center gap-2">
                                        <span
                                            aria-hidden="true"
                                            className="inline-block size-2.5 shrink-0 rounded-[3px]"
                                            style={{
                                                backgroundColor: row.color,
                                            }}
                                        />
                                        <span>{row.label}</span>
                                    </span>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatUsd(row.total)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {PERCENT.format(row.share)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CollapsibleContent>
        </Collapsible>
    );
}
